import { useEffect, useMemo, useState } from "react";
import { ActionPortfolio, InterventionTrend } from "../analytical-charts";
import { useAnalyticalContext } from "../analytical-context";
import { PageHeader } from "../components";
import { focusMatches, interventionMatchesProduct } from "../demo-scope";
import { interventions } from "../fixtures";

export function ActionsPage() {
  const { filters } = useAnalyticalContext();
  const focus =
    new URLSearchParams(window.location.search).get("focus") ?? "delivery";
  const scopedInterventions = useMemo(
    () =>
      interventions.filter((intervention) =>
        interventionMatchesProduct(intervention, filters.product),
      ),
    [filters.product],
  );
  const [selectedId, setSelectedId] = useState(
    () =>
      interventions.find((intervention) =>
        focusMatches(
          `${intervention.title} ${intervention.targetCohort} ${intervention.successMetric}`,
          focus,
        ),
      )?.id ?? interventions[0]!.id,
  );
  useEffect(() => {
    if (
      !scopedInterventions.some(
        (intervention) => intervention.id === selectedId,
      )
    )
      setSelectedId(scopedInterventions[0]!.id);
  }, [scopedInterventions, selectedId]);
  const selected =
    scopedInterventions.find(
      (intervention) => intervention.id === selectedId,
    ) ?? scopedInterventions[0]!;
  const selectedIndex = interventions.findIndex((x) => x.id === selectedId);
  const statusCount = (status: (typeof interventions)[number]["status"]) =>
    scopedInterventions.filter((intervention) => intervention.status === status)
      .length;
  return (
    <>
      <PageHeader
        eyebrow="Actions · What did we change, and did it work?"
        title={
          filters.product === "All"
            ? "One intervention is effective; one remains uncertain."
            : `${selected.title} is ${selected.status.toLowerCase()} in this product scope.`
        }
        description="Investigations, corrective actions and measured interventions stay distinct. Results compare customer outcomes, not task completion."
      />
      {filters.product === "All" && (
        <section className="action-summary">
          <div>
            <strong>{statusCount("Effective")}</strong>
            <span>Effective</span>
          </div>
          <div>
            <strong>{statusCount("Measuring")}</strong>
            <span>Measuring</span>
          </div>
          <div>
            <strong>{statusCount("Inconclusive")}</strong>
            <span>Inconclusive</span>
          </div>
          <div>
            <strong>{statusCount("Approved")}</strong>
            <span>Approved</span>
          </div>
        </section>
      )}
      <div className="coi-grid coi-grid--actions-result">
        <section className="panel intervention-detail">
          <span className="kicker">{selected.type}</span>
          <h2>{selected.title}</h2>
          <p>{selected.measuredResult}</p>
          <dl>
            <div>
              <dt>Owner</dt>
              <dd>{selected.owner}</dd>
            </div>
            <div>
              <dt>Target cohort</dt>
              <dd>{selected.targetCohort}</dd>
            </div>
            <div>
              <dt>Success metric</dt>
              <dd>{selected.successMetric}</dd>
            </div>
            <div>
              <dt>Observation</dt>
              <dd>{selected.observationWindow}</dd>
            </div>
            <div>
              <dt>Baseline</dt>
              <dd>{selected.baseline}</dd>
            </div>
            <div>
              <dt>Target</dt>
              <dd>{selected.target}</dd>
            </div>
          </dl>
          <p>
            <strong>Unintended effects:</strong> {selected.unintendedEffects}
          </p>
        </section>
        <section className="panel analytics-panel">
          <h3>Did the target cohort improve against comparison?</h3>
          <InterventionTrend index={selectedIndex} />
          <p className="chart-insight">
            <span />
            {selected.measuredResult}
          </p>
        </section>
      </div>
      <section className="panel intervention-scorecard">
        <div>
          <span>Success metric</span>
          <strong>{selected.successMetric}</strong>
          <small>Baseline {selected.baseline}</small>
        </div>
        <div>
          <span>Target</span>
          <strong>{selected.target}</strong>
          <small>{selected.status}</small>
        </div>
        {selected.guardrails.map((g) => (
          <div key={g.label}>
            <span>{g.label}</span>
            <strong>{g.actual}</strong>
            <small>
              Guardrail ≤ {g.limit} ·{" "}
              {g.actual <= g.limit ? "within" : "breached"}
            </small>
          </div>
        ))}
        <div>
          <span>Evidence freshness</span>
          <strong>10:24</strong>
          <small>18 Jul 2026</small>
        </div>
      </section>
      <section className="panel intervention-timeline">
        <span>Baseline agreed</span>
        <i />
        <span>{selected.launchDate} · Launch</span>
        <i />
        <span>Stabilisation</span>
        <i />
        <span>{selected.observationWindow} · Decision</span>
      </section>
      <div className="coi-grid coi-grid--wide">
        <section
          className={`panel intervention-index ${filters.product !== "All" ? "intervention-index--scoped" : ""}`}
        >
          <div className="analytics-panel__heading">
            <div>
              <span className="kicker">Intervention portfolio</span>
              <h3>Select another owned operational change</h3>
            </div>
          </div>
          {scopedInterventions.map((item) => (
            <button
              type="button"
              key={item.id}
              className={selected.id === item.id ? "selected" : ""}
              onClick={() => setSelectedId(item.id)}
            >
              <span
                className={`status-text status-text--${item.status.toLowerCase().replace(" ", "-")}`}
              >
                {item.status}
              </span>
              <strong>{item.title}</strong>
              <p>
                {item.type} · {item.owner}
              </p>
              <small>
                {item.targetCohort} · {item.externalReference}
              </small>
            </button>
          ))}
        </section>
        {filters.product === "All" && (
          <section className="panel analytics-panel">
            <h3>Which actions combine impact with strong evidence?</h3>
            <ActionPortfolio />
          </section>
        )}
      </div>
    </>
  );
}
